import React, { useState } from "react";
import FixedRange from "./FixedRange"; // Importar nuestro wrapper
import { useTheme } from "../../context/ThemeContext";

const PriceFilter = ({ priceRange, priceValues, onPriceChange, isLoading = false, customAccentColor, customBackgroundColor, customTextColor }) => {
  const { getBackground, getBorder, getColor, getMuiTextStyle } = useTheme();
  const [minInput, setMinInput] = useState("");
  const [maxInput, setMaxInput] = useState("");
  const [selectedRange, setSelectedRange] = useState("");

  // Colores del tema
  const primaryColor = getColor("primary", "#ae1d22");
  const accentColor = customAccentColor || primaryColor;
  const backgroundColor = customBackgroundColor || getBackground("surface", "#ffffff").backgroundColor;
  const textColor = customTextColor || getColor("filterText", "#111827");
  const secondaryTextColor = getColor("filterTextSecondary", getColor("textSecondary", "#4b5563"));
  const sliderTrackColor = getColor("sliderTrack", "#e9ecef");

  // Función para ajustar al step
  const adjustToStep = (value, step) => {
    return Math.round(value / step) * step;
  };

  // Constante para el step
  const STEP = 5;

  if (isLoading || !priceRange || priceRange.low === undefined || priceRange.high === undefined) {
    return (
      <div
        className="py-2 flex flex-col gap-5 rounded-lg p-4"
        style={{
          ...getBorder("default"),
          backgroundColor: backgroundColor,
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}>
        <h2 className="font-medium text-lg" style={{ color: textColor }}>
          Precio
        </h2>
        <div className="text-center py-4" style={{ color: secondaryTextColor }}>
          Cargando rango de precios...
        </div>
      </div>
    );
  }

  const min = Math.max(priceRange.low, 0);
  const max = Math.max(priceRange.high, min + STEP);
  
  // Ajustar los valores al step
  const adjustedMin = adjustToStep(min, STEP);
  const adjustedMax = adjustToStep(max, STEP);
  
  // Asegurar que los valores actuales estén ajustados al step
  const currentLow = adjustToStep(Math.max(Math.min(priceValues[0], max), min), STEP);
  const currentHigh = adjustToStep(Math.max(Math.min(priceValues[1], max), currentLow + STEP), STEP);

  // Función para manejar cambios ajustando al step
  const handleSliderChange = (values) => {
    const adjustedValues = values.map(value => adjustToStep(value, STEP));
    onPriceChange(adjustedValues);
    setSelectedRange("");
    setMinInput("");
    setMaxInput("");
  };

  // Manejar cambio de inputs manuales
  const handleManualInput = () => {
    const minVal = parseFloat(minInput) || adjustedMin;
    const maxVal = parseFloat(maxInput) || adjustedMax;
    
    if (minVal >= adjustedMin && maxVal <= adjustedMax && minVal < maxVal) {
      onPriceChange([adjustToStep(minVal, STEP), adjustToStep(maxVal, STEP)]);
      setSelectedRange("");
    }
  };

  // Manejar rangos predeterminados
  const handlePredefinedRange = (range) => {
    setSelectedRange(range);
    onPriceChange(range.split("-").map(v => parseFloat(v)));
    setMinInput("");
    setMaxInput("");
  };

  // Generar rangos predeterminados dinámicamente basados en priceRange
  const generatePredefinedRanges = () => {
    const ranges = [];
    const maxPrice = adjustedMax;
    
    const breakpoints = [50, 100, 250, 500, 1000, 2000];
    let previousBreakpoint = 0;
    
    for (let i = 0; i < breakpoints.length; i++) {
      const currentBreakpoint = breakpoints[i];
      
      if (currentBreakpoint <= maxPrice) {
        if (i === 0) {
          ranges.push({
            label: `Menos de S/ ${currentBreakpoint.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            value: `0-${currentBreakpoint}`
          });
        } else {
          ranges.push({
            label: `S/ ${previousBreakpoint.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - S/ ${currentBreakpoint.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            value: `${previousBreakpoint}-${currentBreakpoint}`
          });
        }
        previousBreakpoint = currentBreakpoint;
      } else {
        if (previousBreakpoint < maxPrice) {
          ranges.push({
            label: `S/ ${previousBreakpoint.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - S/ ${Math.ceil(maxPrice).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            value: `${previousBreakpoint}-${Math.ceil(maxPrice)}`
          });
        }
        break;
      }
    }
    
    if (previousBreakpoint < maxPrice && previousBreakpoint === breakpoints[breakpoints.length - 1]) {
      ranges.push({
        label: `S/ ${previousBreakpoint.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - S/ ${Math.ceil(maxPrice).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        value: `${previousBreakpoint}-${Math.ceil(maxPrice)}`
      });
    }
    
    return ranges;
  };

  const predefinedRanges = generatePredefinedRanges();

  // Estilos para la pista del slider
  const trackStyle = {
    backgroundColor: sliderTrackColor,
    borderRadius: "3px",
  };

  // Estilos para los thumb (controladores)
  const thumbStyle = {
    backgroundColor: accentColor,
    border: `2px solid ${getColor("white", "#ffffff")}`,
    boxShadow: `0 2px 4px rgba(0,0,0,0.2)`,
    outline: "none",
  };

  return (
    <div
      className="py-2 flex flex-col gap-5 rounded-lg p-4"
      style={{
        ...getBorder("default"),
        backgroundColor: backgroundColor,
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}>
      <h2 className="font-medium text-lg" style={{ color: textColor }}>
        Precio
      </h2>

      {/* Inputs manuales */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          placeholder="$ Min"
          value={minInput}
          onChange={(e) => setMinInput(e.target.value.replace(/[^0-9.]/g, ''))}
          onBlur={handleManualInput}
          onKeyPress={(e) => e.key === 'Enter' && handleManualInput()}
          className="price-filter-input w-full px-2 py-2 text-sm border rounded focus:outline-none focus:ring-1"
          style={{
            borderColor: accentColor,
            backgroundColor: getBackground("input", "#ffffff").backgroundColor,
            ...getMuiTextStyle("textBody"),
            color: textColor,
            caretColor: accentColor,
            "--tw-ring-color": accentColor,
            "--price-filter-placeholder-color": secondaryTextColor,
          }}
        />
        <input
          type="text"
          placeholder="$ Max"
          value={maxInput}
          onChange={(e) => setMaxInput(e.target.value.replace(/[^0-9.]/g, ''))}
          onBlur={handleManualInput}
          onKeyPress={(e) => e.key === 'Enter' && handleManualInput()}
          className="price-filter-input w-full px-2 py-2 text-sm border rounded focus:outline-none focus:ring-1"
          style={{
            borderColor: accentColor,
            backgroundColor: getBackground("input", "#ffffff").backgroundColor,
            ...getMuiTextStyle("textBody"),
            color: textColor,
            caretColor: accentColor,
            "--tw-ring-color": accentColor,
            "--price-filter-placeholder-color": secondaryTextColor,
          }}
        />
      </div>

      {/* Rangos predeterminados */}
      <div className="space-y-2">
        {predefinedRanges.map((range) => (
          <div key={range.value} className="flex items-center">
            <input
              type="radio"
              id={`price-range-${range.value}`}
              name="price-range"
              value={range.value}
              checked={selectedRange === range.value}
              onChange={() => handlePredefinedRange(range.value)}
              className="w-4 h-4 cursor-pointer"
              style={{ accentColor: accentColor }}
            />
            <label
              htmlFor={`price-range-${range.value}`}
              className="ml-2 text-sm cursor-pointer select-none hover:opacity-80 transition-opacity"
              style={{ ...getMuiTextStyle("textBody"), color: textColor }}>
              {range.label}
            </label>
          </div>
        ))}
      </div>

      <FixedRange
        step={STEP}
        min={adjustedMin}
        max={adjustedMax}
        values={[currentLow, currentHigh]}
        onChange={handleSliderChange}
        renderTrack={({ props, children, isDragged }) => {
          const { key, ...restProps } = props;
          return (
            <div
              key={key}
              {...restProps}
              className="w-full h-[6px] rounded-full cursor-pointer"
              style={{
                ...restProps.style,
                ...trackStyle,
              }}>
              {children}
            </div>
          );
        }}
        renderThumb={({ props, index, isDragged }) => {
          const { key, ...restProps } = props;
          return (
            <div
              key={key}
              {...restProps}
              className="w-[20px] h-[20px] rounded-full cursor-pointer focus:outline-none focus:ring-2"
              style={{
                ...restProps.style,
                ...thumbStyle,
                ...(isDragged && {
                  boxShadow: `0 0 0 3px ${accentColor}40`,
                  transform: "scale(1.1)",
                }),
              }}
              aria-label={`Controlador de precio ${index === 0 ? 'mínimo' : 'máximo'}`}
            />
          );
        }}
      />

      <div>
        <span
          className="font-bold text-lg"
          style={{
            ...getMuiTextStyle("textPrimary"),
            color: textColor,
          }}>
          S/{Math.floor(currentLow)} - S/{Math.floor(currentHigh)}
        </span>
      </div>
    </div>
  );
};

export default PriceFilter;
